import React, { Suspense } from 'react';
import { Switch } from 'react-router-dom';

import AppHeader from 'components/appHeader/AppHeader.js';
import AppRoute from 'components/routers/AppRoute';
import AppUpdateHandler from './components/AppUpdateHandler.js';
import EventHandler from './components/EventHandler.js';
import EscapeKeyHandler from './components/EscapeKeyHandler';
import ImagePopup from './components/ImagePopup.js';
import ProfileSlider from 'screens/profile/ProfileSlider.js';
import TooltipHandler from './components/TooltipHandler.js';
import OauthScreen from 'screens/oauth/OauthScreen.js';
import PipelineKanbanLoader from './screens/pipeline/pipelineKanban/PipelineKanbanLoader.js';
import LeadModalLoader from './components/LeadModalLoader.js';
import { ErrorBoundary } from 'components/ErrorBoundary.js';
import { Toasts } from 'components/Toasts';
import NotificationSlider from 'components/NotificationSlider.js';
import { useSelector } from 'react-redux';
import FullStory from 'react-fullstory';
import PublishRealtimeRecordViewing from 'components/PublishRealTimeRecordViewing.js';
import { INBOX_ACCESS, PIPELINES_ACCESS, SETTINGS_ACCESS } from './lib/constants/permissions';

const DashboardScreen = React.lazy(() => import('screens/dashboard/DashboardScreen.js'));
const CommunicationLogsScreen = React.lazy(() => import('screens/communicationLogs/CommunicationLogsScreen'));
const ResetPasswordScreen = React.lazy(() => import('screens/login/ResetPasswordScreen.js'));
const SettingsScreen = React.lazy(() => import('screens/settings/SettingsScreen.js'));
const TodosScreen = React.lazy(() => import('screens/todos/TodosScreen.js'));
const InboxScreen = React.lazy(() => import('screens/inbox/InboxScreen'));
const PipelineScreen = React.lazy(() => import('screens/pipeline/PipelineScreen.js'));
const PlaygroundScreen = React.lazy(() => import('./screens/playground/PlaygroundScreen.js'));
const PrivacyPolicyScreen = React.lazy(() => import('./screens/privacyPolicy/PrivacyPolicyScreen.js'));
const LoginScreen = React.lazy(() => import('screens/login/LoginScreen'));
const ReportsScreen = React.lazy(() => import('screens/reports/ReportsScreen.js'));
const CRMReportsScreen = React.lazy(() => import('screens/reports/CRMReportsScreen.js'));
const HelpScreen = React.lazy(() => import('screens/help/HelpScreen.js'));
const LeadLoopReportsScreen = React.lazy(() => import('screens/reports/LeadLoopReportsScreen.js'));
const ORG_ID = 'o-1CAREB-na1';
const isApple = (/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform) || /(iPhone|iPod|iPad)/i.test(navigator.platform)) && window.location.hostname !== 'localhost';

const App = () => {
  const loggedIn = useSelector((state) => state.user.loggedIn);
  const userId = useSelector((state) => state.user.id);

  return (
    <ErrorBoundary>
      <div className={`app-container user-${userId} ${isApple ? 'apple' : ''}`}>
        <AppHeader />
        <Suspense fallback={<div />}>
          <FullStory org={ORG_ID} />
          <Switch>
            <AppRoute isProtected={true} path="/activity" title={'Leadloop Reporting'} exact component={LeadLoopReportsScreen} />
            <AppRoute isProtected={true} path="/help/:tab?/:tab2?" title={'Help'} component={HelpScreen} fixedWidth={true} />
            <AppRoute isProtected={true} path="/:serviceProviderId/inbox/:tab?" title={'Inbox'} component={InboxScreen} />
            <AppRoute isProtected={true} path="/:serviceProviderId/pipelines/:pipelineId?" title={'Pipelines'} component={PipelineScreen} loaderComponent={PipelineKanbanLoader} />
            <AppRoute isProtected={true} path="/:serviceProviderId/todos" title={'Todos'} component={TodosScreen} />
            <AppRoute isProtected={true} path="/:serviceProviderId/settings/:view?/:detail?" title={'Settings'} component={SettingsScreen}/>
            <AppRoute isProtected={true} path="/:serviceProviderId/activity/:activity?" title={'Activity'} component={CommunicationLogsScreen} />
            <AppRoute isProtected={true} path="/:serviceProviderId/playground/:view?" title={'Playground'} component={PlaygroundScreen} />
            <AppRoute isProtected={true} path="/oauth/callback" exact title={'Oauth'} component={OauthScreen} />
            <AppRoute isProtected={true} path="/dashboard/:tab?" title={'Dashboard'} component={DashboardScreen} />
            <AppRoute isProtected={false} path="/privacy-policy" exact title={'Privacy Policy'} component={PrivacyPolicyScreen} />
            <AppRoute isProtected={true} path="/:serviceProviderId/reports/:reportType/:detailsView?" title={'Reporting'} component={ReportsScreen} />
            <AppRoute isProtected={false} path="/report/:hash?/:reportType?/:subReportType?" exact title={'CRM Reporting'} component={CRMReportsScreen} />
            <AppRoute isProtected={false} path="/reset/:hash" exact title={'Reset Password'} component={ResetPasswordScreen} />
            <AppRoute isProtected={false} path="/login" title={'Login'} exact component={LoginScreen} />
            <AppRoute isProtected={false} path="/" title={'Login'} exact component={LoginScreen} />
            <AppRoute isProtected={false} path="/" title={'Login'} is404={true} component={LoginScreen} />
          </Switch>
        </Suspense>
        <LeadModalLoader />
        <ProfileSlider />
        {loggedIn && <NotificationSlider />}
        <Toasts />
        <ImagePopup />
        <TooltipHandler />
        <EventHandler />
        <PublishRealtimeRecordViewing />
        <EscapeKeyHandler />
        <AppUpdateHandler />
      </div>
    </ErrorBoundary>
  );
};

export default App;
